import React, { useEffect, useState, useRef } from "react";
import {
  ClipboardCopy,
  DollarSign,
  Users,
  ExternalLink,
  TrendingUp,
  BarChart2,
  Activity,
  CheckCircle,
  CreditCard,
  History,
  AlertCircle,
  Banknote,
  LineChart as LineChartIcon,
} from "lucide-react";
import { formatCurrency } from "../../utils/currency";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { Select } from "../../components/common/Select";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import { useGlobalState } from "../../GlobalStates";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getReferralAmount } from "../../utils/multi-language";
import { useUserLocale } from "../../customHooks/useUserLocale";
import { StickyHeader } from "../../intro/components/landing-new";
import { motion, AnimatePresence } from "framer-motion";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
  Bar,
  ComposedChart,
} from "recharts";
import { Select as CustomSelect } from "../../components/common/Select";
import {
  getAffiliateAnalytics,
  getAffiliateReferrals,
  getAffiliateEarningsSummary,
  requestWithdrawal,
  getWithdrawals,
  getAffiliateBalances,
} from "../../services/affiliate";
import { useIsAuthenticated } from "../../utils/auth";
import dayjs from "dayjs";

const WITHDRAWAL_METHOD_MAP = {
  bank_transfer: {
    label: "Bank Transfer",
    icon: <CreditCard className="tw-h-4 tw-w-4 tw-text-blue-600" />,
    className: "tw-text-blue-600",
  },
};

const AffiliateProgram = () => {
  const [user] = useGlobalState("user");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currency } = useUserLocale();
  const { t } = useTranslation();
  const { isLoading: loadingAuth } = useIsAuthenticated();
  const [inviteUrl, setInviteUrl] = useState(`${window.location.origin}/welcome-discount`);
  const [inviteMessage, setInviteMessage] = useState("");
  const [copyIconColor, setCopyIconColor] = useState("black");
  const [_, setOpenAlertBannerMsg] = useGlobalState("openAlertBannerMsg");

  const [analyticsData, setAnalyticsData] = useState({
    daily: [],
    weekly: [],
    monthly: [],
    all_time: [],
  });
  const [referrals, setReferrals] = useState([]);
  const [referralTotal, setReferralTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 10;
  const [earningsSummary, setEarningsSummary] = useState({
    totalEarnings: "0.00",
    totalClicks: 0,
    totalSignups: 0,
    totalBookings: 0,
    signupRate: "0.0",
    bookingRate: "0.0",
  });

  const timeframe = searchParams.get("timeframe") || "all_time";
  const setTimeframe = (newTimeframe) => {
    setSearchParams({ ...Object.fromEntries(searchParams), timeframe: newTimeframe });
  };

  const [isLoading, setIsLoading] = useState(true);
  const canAccessWithdrawal = user?.influencer_type === "Popular duck";

  const changeCopyColor = () => {
    setOpenAlertBannerMsg(t("affiliate.affiliate_link.copied"));
    setCopyIconColor("deepskyblue");
    setTimeout(() => setCopyIconColor("black"), 1200);
  };

  useEffect(() => {
    const getUserInviteLink = async () => {
      if (user.id) {
        const inviteUrl = `${window.location.origin}/welcome-discount?code=${user.id}`;
        const msg = t("invite_message", {
          defaultValue: `Use this link to get {{amount}} off your first Sotravel trip! 👉 ${inviteUrl}`,
          amount: getReferralAmount(currency, "welcome"),
          inviteUrl: inviteUrl,
        });
        setInviteMessage(msg);
        setInviteUrl(inviteUrl);
      }
    };
    getUserInviteLink();
  }, [user.id, currency, t]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user.id) return;
      setIsLoading(true);
      try {
        const { data: analytics } = await getAffiliateAnalytics(timeframe);
        const formattedAnalytics = analytics.map((item) => ({
          date: item.date || "",
          clicks: parseInt(item.clicks || 0),
          signups: parseInt(item.signups || 0),
          bookings: parseInt(item.bookings || 0),
          earningsCash: parseFloat(item.earningsCash || 0).toFixed(2),
          earningsCredits: parseFloat(item.earningsCredits || 0).toFixed(2),
          signupRate: item.signupRate || "0.0",
          bookingRate: item.bookingRate || "0.0",
        }));
        setAnalyticsData((prevData) => ({ ...prevData, [timeframe]: formattedAnalytics }));

        const { data: referralsData } = await getAffiliateReferrals({
          limit: pageSize,
          offset: currentPage * pageSize,
        });
        setReferrals(referralsData.referrals);
        setReferralTotal(referralsData.total);

        const { data: summary } = await getAffiliateEarningsSummary();
        setEarningsSummary({
          totalEarnings: parseFloat(summary.totalEarnings || 0).toFixed(2),
          totalClicks: parseInt(summary.totalClicks || 0),
          totalSignups: parseInt(summary.totalSignups || 0),
          totalBookings: parseInt(summary.totalBookings || 0),
          signupRate: summary.signupRate || "0.0",
          bookingRate: summary.bookingRate || "0.0",
        });
      } catch (error) {
        console.error("Error fetching affiliate data:", error);
        setOpenAlertBannerMsg("Failed to load affiliate data. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [user.id, timeframe, currentPage, setOpenAlertBannerMsg]);

  const handlePageChange = (newPage) => setCurrentPage(newPage);

  if (loadingAuth) return <LoadingSkeleton />;

  return (
    <div className="tw-min-h-screen tw-bg-gray-50">
      <StickyHeader withoutCta />
      <HeroSection user={user} navigate={navigate} t={t} />
      <div className="tw-mx-auto tw-max-w-6xl tw-px-4 tw-py-8">
        {!user.id ? (
          <>
            <HowItWorksSection currency={currency} t={t} />
            <BenefitsSection currency={currency} t={t} />
          </>
        ) : (
          <>
            <AffiliateLinkSection
              inviteUrl={inviteUrl}
              inviteMessage={inviteMessage}
              changeCopyColor={changeCopyColor}
              copyIconColor={copyIconColor}
              t={t}
            />
            <AnalyticsSection
              isLoading={isLoading}
              timeframe={timeframe}
              setTimeframe={setTimeframe}
              analyticsData={analyticsData}
              earningsSummary={earningsSummary}
              referrals={referrals}
              referralTotal={referralTotal}
              currentPage={currentPage}
              onPageChange={handlePageChange}
              pageSize={pageSize}
              t={t}
            />
            {canAccessWithdrawal && (
              <WithdrawalSection
                earningsSummary={earningsSummary}
                setOpenAlertBannerMsg={setOpenAlertBannerMsg}
                t={t}
              />
            )}
          </>
        )}
        <footer className="tw-mt-12 tw-border-t tw-border-gray-200 tw-pt-8 tw-text-center">
          <p className="tw-text-gray-600">{t("affiliate.footer")}</p>
        </footer>
      </div>
    </div>
  );
};

const ReferralsTable = ({ referrals, total, currentPage, onPageChange, pageSize, t }) => {
  const getAffiliateSourceText = (source) => {
    switch (source) {
      case "affiliate_link":
        return t("affiliate.analytics.referrals.columns.source_affiliate_link", {
          defaultValue: "Affiliate link",
        });
      case "promo_affiliate":
        return t("affiliate.analytics.referrals.columns.source_promo_affiliate", {
          defaultValue: "Promo Code",
        });
      default:
        return source;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "signed_up":
        return "tw-bg-purple-50 tw-text-purple-600";
      case "completed_booking":
        return "tw-bg-green-50 tw-text-green-600";
      case "cancelled":
        return "tw-bg-red-50 tw-text-red-600";
      default:
        return "tw-bg-gray-50 tw-text-gray-600";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "signed_up":
        return t("affiliate.analytics.referrals.status.signed_up");
      case "completed_booking":
        return t("affiliate.analytics.referrals.status.completed_booking");
      case "cancelled":
        return t("affiliate.analytics.referrals.status.cancelled");
      default:
        return t("affiliate.analytics.referrals.status.not_completed");
    }
  };

  const formatCommission = (amount, currency, claim_type) => {
    if (!claim_type) return t("affiliate.analytics.referrals.columns.na", { defaultValue: "N/A" });
    return formatCurrency(amount, currency);
  };

  const totalPages = Math.ceil(total / pageSize);

  const getPageNumbers = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];
    let l;

    range.push(1);
    for (let i = currentPage - delta; i <= currentPage + delta + 1; i++) {
      if (i < totalPages && i > 1) range.push(i);
    }
    if (totalPages > 1) range.push(totalPages);

    for (let i of range) {
      if (l) {
        if (i - l === 2) rangeWithDots.push(l + 1);
        else if (i - l !== 1) rangeWithDots.push("...");
      }
      rangeWithDots.push(i);
      l = i;
    }
    return rangeWithDots;
  };

  return (
    <div className="tw-space-y-4">
      <div className="tw-w-full tw-overflow-x-auto">
        <table className="tw-w-full tw-table-fixed tw-border-collapse">
          <colgroup>
            <col className="tw-w-[100px]" />
            <col className="tw-w-[120px]" />
            <col className="tw-w-[250px]" />
            <col className="tw-w-[120px]" />
            <col className="tw-w-[120px]" />
            <col className="tw-w-[100px]" />
            <col className="tw-w-[120px]" />
          </colgroup>
          <thead>
            <tr className="tw-border-b tw-border-gray-200">
              <th className="tw-px-4 tw-py-4 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-600">
                {t("affiliate.analytics.referrals.columns.status")}
              </th>
              <th className="tw-px-4 tw-py-4 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-600">
                {t("affiliate.analytics.referrals.columns.name")}
              </th>
              <th className="tw-px-4 tw-py-4 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-600">
                {t("affiliate.analytics.referrals.columns.trip")}
              </th>
              <th className="tw-px-4 tw-py-4 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-600">
                {t("affiliate.analytics.referrals.columns.source")}
              </th>
              <th className="tw-px-4 tw-py-4 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-600">
                {t("affiliate.analytics.referrals.columns.booking_date")}
              </th>
              <th className="tw-px-4 tw-py-4 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-600">
                {t("affiliate.analytics.referrals.columns.claimed")}
              </th>
              <th className="tw-px-4 tw-py-4 tw-text-right tw-text-sm tw-font-semibold tw-text-gray-600">
                {t("affiliate.analytics.referrals.columns.earned")}
              </th>
            </tr>
          </thead>
          <tbody>
            {referrals.map((referral, index) => (
              <motion.tr
                key={referral.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="tw-border-b tw-border-gray-100 tw-transition-colors hover:tw-bg-gray-50"
              >
                <td className="tw-whitespace-nowrap tw-px-4 tw-py-4">
                  <span
                    className={`tw-rounded-full tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium ${getStatusColor(referral.status)}`}
                  >
                    {getStatusText(referral.status)}
                  </span>
                </td>
                <td className="tw-truncate tw-px-4 tw-py-4 tw-text-sm tw-text-gray-900">
                  {referral.user}
                </td>
                <td className="tw-px-4 tw-py-4 tw-text-sm tw-text-gray-900">
                  <div
                    className="tw-truncate"
                    title={
                      referral.trip || t("affiliate.analytics.referrals.columns.no_trip_booked_yet")
                    }
                  >
                    {referral.trip || t("affiliate.analytics.referrals.columns.no_trip_booked_yet")}
                  </div>
                </td>
                <td className="tw-whitespace-nowrap tw-px-4 tw-py-4 tw-text-sm tw-text-gray-600">
                  {getAffiliateSourceText(referral.affiliate_source)}
                </td>
                <td className="tw-whitespace-nowrap tw-px-4 tw-py-4 tw-text-sm">
                  {(() => {
                    if (!referral.bookingDate) {
                      return t("affiliate.analytics.referrals.columns.not_booked", {
                        defaultValue: "Not booked",
                      });
                    }
                    const parsed = dayjs(referral.bookingDate);
                    return parsed.isValid()
                      ? parsed.format("DD MMM YYYY")
                      : t("affiliate.analytics.referrals.columns.not_booked", {
                          defaultValue: "Not booked",
                        });
                  })()}
                </td>
                <td className="tw-whitespace-nowrap tw-px-4 tw-py-4">
                  {!referral.claim_type ? (
                    <span className="tw-rounded-full tw-bg-gray-50 tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-text-gray-600">
                      {t("affiliate.analytics.referrals.columns.na", { defaultValue: "N/A" })}
                    </span>
                  ) : referral.claimed && (!referral.amount || referral.amount === 0) ? (
                    // Visibility-only backfill row (claim_amount=0, claimed=true).
                    // Nothing was actually disbursed — show a neutral marker
                    // instead of the misleading "Disbursed" badge.
                    <span className="tw-text-xs tw-text-gray-400">—</span>
                  ) : (
                    <span
                      className={`tw-rounded-full tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium ${referral.withdrawing ? "tw-bg-orange-50 tw-text-orange-600" : referral.claimed || referral.status == "completed_booking" ? "tw-bg-green-50 tw-text-green-600" : "tw-bg-yellow-50 tw-text-yellow-600"}`}
                    >
                      {referral.withdrawing
                        ? t("affiliate.analytics.referrals.status.withdrawing")
                        : referral.claimed
                          ? t("affiliate.analytics.referrals.status.disbursed")
                          : referral.status == "not_completed_booking"
                            ? t("affiliate.analytics.referrals.status.not_ready")
                            : t("affiliate.analytics.referrals.status.ready")}
                    </span>
                  )}
                </td>
                <td className="tw-whitespace-nowrap tw-px-4 tw-py-4 tw-text-right tw-text-sm tw-font-medium tw-text-gray-900">
                  {formatCommission(referral.amount, referral.currency, referral.claim_type)}
                </td>
              </motion.tr>
            ))}
            {referrals.length === 0 && (
              <tr>
                <td colSpan="8" className="tw-py-8 tw-text-center tw-text-gray-500">
                  {t("affiliate.analytics.referrals.empty")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="tw-mt-4 tw-flex tw-items-center tw-justify-between">
          <div className="tw-text-sm tw-text-gray-600">
            {t("affiliate.analytics.referrals.showing", {
              defaultValue: "Showing {{start}} to {{end}} of {{total}} entries",
              start: Math.min(currentPage * pageSize + 1, total),
              end: Math.min((currentPage + 1) * pageSize, total),
              total,
            })}
          </div>
          <div className="tw-flex tw-items-center tw-gap-1">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 0}
              className={`tw-flex tw-h-[32px] tw-min-w-[32px] tw-items-center tw-justify-center tw-rounded-md tw-px-2 tw-py-1 tw-text-sm ${currentPage === 0 ? "tw-cursor-not-allowed tw-bg-gray-100 tw-text-gray-400" : "tw-border tw-border-gray-300 tw-bg-white tw-text-gray-700 hover:tw-bg-gray-50"}`}
              aria-label={t("affiliate.analytics.referrals.previous_page", {
                defaultValue: "Previous page",
              })}
            >
              ←
            </button>
            {getPageNumbers().map((pageNum, index) => (
              <button
                key={index}
                onClick={() => pageNum !== "..." && onPageChange(pageNum - 1)}
                disabled={pageNum === "..."}
                className={`tw-flex tw-h-[32px] tw-min-w-[32px] tw-items-center tw-justify-center tw-rounded-md tw-px-2 tw-py-1 tw-text-sm ${pageNum === currentPage + 1 ? "tw-bg-blue-500 tw-text-white" : pageNum === "..." ? "tw-cursor-default tw-border-none tw-bg-white tw-text-gray-400" : "tw-border tw-border-gray-300 tw-bg-white tw-text-gray-700 hover:tw-bg-gray-50"}`}
              >
                {pageNum}
              </button>
            ))}
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages - 1}
              className={`tw-flex tw-h-[32px] tw-min-w-[32px] tw-items-center tw-justify-center tw-rounded-md tw-px-2 tw-py-1 tw-text-sm ${currentPage >= totalPages - 1 ? "tw-cursor-not-allowed tw-bg-gray-100 tw-text-gray-400" : "tw-border tw-border-gray-300 tw-bg-white tw-text-gray-700 hover:tw-bg-gray-50"}`}
              aria-label={t("affiliate.analytics.referrals.next_page", {
                defaultValue: "Next page",
              })}
            >
              →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const WithdrawalSection = ({ earningsSummary, setOpenAlertBannerMsg, t }) => {
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [withdrawalHistory, setWithdrawalHistory] = useState([]);
  const [balances, setBalances] = useState({
    availableBalance: "0.00",
    pendingRelease: "0.00",
    totalWithdrawn: "0.00",
  });
  const [isLoading, setIsLoading] = useState(true);

  const fetchWithdrawals = async () => {
    try {
      const { data } = await getWithdrawals();
      setWithdrawalHistory(data);
    } catch (error) {
      console.error("Error fetching withdrawals:", error);
      setOpenAlertBannerMsg(
        t("affiliate.withdraw.history.error", { defaultValue: "Failed to load withdrawal history" })
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchBalances = async () => {
    try {
      const { data } = await getAffiliateBalances();
      setBalances(data);
    } catch (error) {
      console.error("Error fetching balances:", error);
      setOpenAlertBannerMsg(
        t("affiliate.withdraw.balances.error", {
          defaultValue: "Failed to load balance information",
        })
      );
    }
  };

  useEffect(() => {
    fetchWithdrawals();
    fetchBalances();
  }, []);

  const hasPendingWithdrawal = withdrawalHistory.some((w) => w.status === "pending");

  return (
    <section className="tw-mb-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="tw-rounded-xl tw-bg-white tw-p-6 tw-shadow-sm">
          <div className="tw-mb-6 tw-flex tw-items-center tw-justify-between">
            <h2 className="tw-text-deepskyblue tw-text-2xl tw-font-semibold">
              {t("affiliate.withdraw.title")}
            </h2>
            <button
              onClick={() => setShowWithdrawalModal(true)}
              disabled={Number(balances.availableBalance) === 0 || hasPendingWithdrawal}
              className={`tw-rounded-full tw-bg-[deepskyblue] tw-px-8 tw-py-2.5 tw-font-medium tw-text-white tw-transition-all hover:tw-shadow-lg ${Number(balances.availableBalance) === 0 || hasPendingWithdrawal ? "tw-cursor-not-allowed tw-opacity-50" : "hover:tw-bg-[#00a3d9]"}`}
              title={
                hasPendingWithdrawal
                  ? t("affiliate.withdraw.pending_title")
                  : Number(balances.availableBalance) === 0
                    ? t("affiliate.withdraw.no_earnings")
                    : t("affiliate.withdraw.request")
              }
            >
              {t("affiliate.withdraw.withdraw_button")}
            </button>
          </div>
          {hasPendingWithdrawal && (
            <div className="tw-mb-6 tw-flex tw-items-start tw-gap-3 tw-rounded-lg tw-bg-blue-50 tw-p-4 tw-text-blue-700">
              <AlertCircle className="tw-mt-0.5 tw-h-5 tw-w-5 tw-flex-shrink-0" />
              <div>
                <p className="tw-font-medium">{t("affiliate.withdraw.pending_title")}</p>
                <p className="tw-mt-1 tw-text-sm">{t("affiliate.withdraw.pending_message")}</p>
              </div>
            </div>
          )}
          <div className="tw-mb-8 tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-3">
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="tw-rounded-xl tw-bg-gradient-to-r tw-from-blue-500 tw-to-blue-600 tw-p-6 tw-text-white tw-shadow-sm"
            >
              <div className="tw-mb-2 tw-flex tw-items-center tw-gap-3">
                <div className="tw-rounded-lg tw-bg-white/10 tw-p-2">
                  <DollarSign className="tw-h-5 tw-w-5 tw-text-white" />
                </div>
                <h3>{t("affiliate.withdraw.ready_to_withdraw")}</h3>
              </div>
              <p className="tw-text-2xl tw-font-bold">${balances.availableBalance}</p>
              <p className="tw-text-sm tw-text-blue-100">
                {t("affiliate.withdraw.ready_to_withdraw")}
              </p>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="tw-rounded-xl tw-border tw-border-gray-100 tw-bg-white tw-p-6 tw-shadow-sm"
            >
              <div className="tw-mb-2 tw-flex tw-items-center tw-gap-3">
                <div className="tw-rounded-lg tw-bg-purple-50 tw-p-2">
                  <History className="tw-h-5 tw-w-5 tw-text-purple-500" />
                </div>
                <h3 className="tw-text-gray-600">{t("affiliate.withdraw.pending_release")}</h3>
              </div>
              <p className="tw-text-2xl tw-font-bold">${balances.pendingRelease}</p>
              <p className="tw-text-sm tw-text-gray-500">
                {t("affiliate.withdraw.pending_description")}
              </p>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="tw-rounded-xl tw-border tw-border-gray-100 tw-bg-white tw-p-6 tw-shadow-sm"
            >
              <div className="tw-mb-2 tw-flex tw-items-center tw-gap-3">
                <div className="tw-rounded-lg tw-bg-green-50 tw-p-2">
                  <Banknote className="tw-h-5 tw-w-5 tw-text-green-500" />
                </div>
                <h3 className="tw-text-gray-600">{t("affiliate.withdraw.total_withdrawn")}</h3>
              </div>
              <p className="tw-text-2xl tw-font-bold">${balances.totalWithdrawn}</p>
              <p className="tw-text-sm tw-text-gray-500">
                {t("affiliate.withdraw.total_withdrawn_description")}
              </p>
            </motion.div>
          </div>
          <div className="tw-mt-8">
            <h3 className="tw-mb-4 tw-text-lg tw-font-semibold">
              {t("affiliate.withdraw.history.title")}
            </h3>
            <div className="tw-overflow-x-auto">
              {isLoading ? (
                <div className="tw-flex tw-items-center tw-justify-center tw-py-8">
                  <p className="tw-text-gray-500">
                    {t("affiliate.withdraw.history.loading", {
                      defaultValue: "Loading withdrawal history...",
                    })}
                  </p>
                </div>
              ) : (
                <table className="tw-w-full tw-border-collapse">
                  <thead>
                    <tr className="tw-border-b tw-border-gray-200">
                      <th className="tw-px-4 tw-py-4 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-600">
                        {t("affiliate.withdraw.history.columns.id", { defaultValue: "ID" })}
                      </th>
                      <th className="tw-px-4 tw-py-4 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-600">
                        {t("affiliate.withdraw.history.columns.date")}
                      </th>
                      <th className="tw-w-[140px] tw-px-4 tw-py-4 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-600">
                        {t("affiliate.withdraw.history.columns.amount")}
                      </th>
                      <th className="tw-w-[180px] tw-px-4 tw-py-4 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-600">
                        {t("affiliate.withdraw.history.columns.method")}
                      </th>
                      <th className="tw-px-4 tw-py-4 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-600">
                        {t("affiliate.withdraw.history.columns.status")}
                      </th>
                      <th className="tw-px-4 tw-py-4 tw-text-left tw-text-sm tw-font-semibold tw-text-gray-600">
                        {t("affiliate.withdraw.history.columns.estimated_arrival")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {withdrawalHistory.map((withdrawal, index) => (
                      <motion.tr
                        key={withdrawal.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        className="tw-border-b tw-border-gray-100 tw-transition-colors hover:tw-bg-gray-50"
                      >
                        <td className="tw-px-4 tw-py-4 tw-text-sm tw-font-medium tw-text-gray-900">
                          {withdrawal.id}
                        </td>
                        <td className="tw-px-4 tw-py-4 tw-text-sm tw-text-gray-600">
                          {withdrawal.requestDate}
                        </td>
                        <td className="tw-px-4 tw-py-4 tw-text-sm tw-font-medium tw-text-gray-900">
                          ${withdrawal.amount}
                        </td>
                        <td className="tw-w-[180px] tw-px-4 tw-py-4">
                          <span
                            className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-text-sm ${WITHDRAWAL_METHOD_MAP[withdrawal.method]?.className || "tw-text-gray-600"}`}
                          >
                            {WITHDRAWAL_METHOD_MAP[withdrawal.method]?.icon}
                            {WITHDRAWAL_METHOD_MAP[withdrawal.method]?.label || withdrawal.method}
                          </span>
                        </td>
                        <td className="tw-px-4 tw-py-4">
                          <span
                            className={`tw-rounded-full tw-px-3 tw-py-1 tw-text-xs tw-font-medium ${withdrawal.status === "completed" ? "tw-bg-green-50 tw-text-green-600" : "tw-bg-blue-50 tw-text-blue-600"}`}
                          >
                            {t(`affiliate.withdraw.history.status.${withdrawal.status}`)}
                          </span>
                        </td>
                        <td className="tw-px-4 tw-py-4 tw-text-sm tw-text-gray-600">
                          {withdrawal.estimatedArrival ?? "-"}
                        </td>
                      </motion.tr>
                    ))}
                    {withdrawalHistory.length === 0 && !isLoading && (
                      <tr>
                        <td colSpan="6" className="tw-py-8 tw-text-center tw-text-gray-500">
                          {t("affiliate.withdraw.history.empty")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </motion.div>
      {showWithdrawalModal && (
        <WithdrawalModal
          onClose={() => setShowWithdrawalModal(false)}
          availableBalance={balances.availableBalance}
          setOpenAlertBannerMsg={setOpenAlertBannerMsg}
          onSuccess={() => {
            fetchWithdrawals();
            fetchBalances();
          }}
          t={t}
        />
      )}
    </section>
  );
};

const WithdrawalModal = ({ onClose, availableBalance, setOpenAlertBannerMsg, onSuccess, t }) => {
  const [bankDetails, setBankDetails] = useState({
    accountName: "",
    accountNumber: "",
    bankName: "",
    swiftCode: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const modalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) onClose();
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const handleWithdrawalRequest = async () => {
    try {
      setIsSubmitting(true);
      await requestWithdrawal({ bankDetails });
      onClose();
      setOpenAlertBannerMsg(t("affiliate.withdraw.withdraw_modal.success"));
      onSuccess();
    } catch (error) {
      console.error("Error requesting withdrawal:", error);
      setOpenAlertBannerMsg(
        error.response?.data?.error ||
          t("affiliate.withdraw.withdraw_modal.error", {
            defaultValue: "Failed to submit withdrawal request",
          })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black/50">
      <motion.div
        ref={modalRef}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="tw-mx-4 tw-w-full tw-max-w-md tw-rounded-xl tw-bg-white tw-p-6"
      >
        <h3 className="tw-mb-4 tw-text-xl tw-font-semibold">
          {t("affiliate.withdraw.withdraw_modal.title")}
        </h3>
        <div>
          <div className="tw-space-y-4">
            <div className="tw-mb-4 tw-rounded-lg tw-bg-blue-50 tw-p-4">
              <p className="tw-font-medium tw-text-blue-700">
                {t("affiliate.withdraw.available_balance")}
              </p>
              <p className="tw-text-2xl tw-font-bold tw-text-blue-900">${availableBalance}</p>
              <p className="tw-mt-1 tw-text-sm tw-text-blue-600">
                {t("affiliate.withdraw.withdraw_modal.via_bank", {
                  defaultValue: "Via bank transfer (2-3 business days)",
                })}
              </p>
            </div>
            <div>
              <label className="tw-mb-1 tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                {t("affiliate.withdraw.withdraw_modal.account_name")}
              </label>
              <input
                type="text"
                value={bankDetails.accountName}
                onChange={(e) => setBankDetails({ ...bankDetails, accountName: e.target.value })}
                placeholder={t("affiliate.withdraw.withdraw_modal.account_name_placeholder")}
                className="tw-w-full tw-rounded-md tw-border tw-border-gray-300 tw-p-2"
              />
            </div>
            <div>
              <label className="tw-mb-1 tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                {t("affiliate.withdraw.withdraw_modal.account_number")}
              </label>
              <input
                type="text"
                value={bankDetails.accountNumber}
                onChange={(e) => setBankDetails({ ...bankDetails, accountNumber: e.target.value })}
                placeholder={t("affiliate.withdraw.withdraw_modal.account_number_placeholder")}
                className="tw-w-full tw-rounded-md tw-border tw-border-gray-300 tw-p-2"
              />
            </div>
            <div>
              <label className="tw-mb-1 tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                {t("affiliate.withdraw.withdraw_modal.bank_name")}
              </label>
              <input
                type="text"
                value={bankDetails.bankName}
                onChange={(e) => setBankDetails({ ...bankDetails, bankName: e.target.value })}
                placeholder={t("affiliate.withdraw.withdraw_modal.bank_name_placeholder")}
                className="tw-w-full tw-rounded-md tw-border tw-border-gray-300 tw-p-2"
              />
            </div>
            <div>
              <label className="tw-mb-1 tw-block tw-text-sm tw-font-medium tw-text-gray-700">
                {t("affiliate.withdraw.withdraw_modal.swift_code")}
              </label>
              <input
                type="text"
                value={bankDetails.swiftCode}
                onChange={(e) => setBankDetails({ ...bankDetails, swiftCode: e.target.value })}
                placeholder={t("affiliate.withdraw.withdraw_modal.swift_code_placeholder", {
                  defaultValue: "Enter SWIFT code",
                })}
                className="tw-w-full tw-rounded-md tw-border tw-border-gray-300 tw-p-2"
              />
            </div>
          </div>
          <div className="tw-mt-6 tw-flex tw-gap-3">
            <button
              onClick={onClose}
              className="tw-rounded-full tw-bg-gray-100 tw-px-6 tw-py-2.5 tw-font-medium tw-text-gray-700 tw-transition-all hover:tw-bg-gray-200"
            >
              {t("affiliate.withdraw.withdraw_modal.cancel")}
            </button>
            <button
              onClick={handleWithdrawalRequest}
              disabled={
                !bankDetails.accountName ||
                !bankDetails.accountNumber ||
                !bankDetails.bankName ||
                !bankDetails.swiftCode ||
                isSubmitting
              }
              className={`tw-flex-1 tw-rounded-full tw-bg-[deepskyblue] tw-px-6 tw-py-2.5 tw-font-medium tw-text-white tw-transition-all hover:tw-shadow-lg ${!bankDetails.accountName || !bankDetails.accountNumber || !bankDetails.bankName || !bankDetails.swiftCode || isSubmitting ? "tw-cursor-not-allowed tw-opacity-50" : "hover:tw-bg-[#00a3d9]"}`}
            >
              {isSubmitting
                ? t("affiliate.withdraw.withdraw_modal.submitting", {
                    defaultValue: "Submitting...",
                  })
                : t("affiliate.withdraw.withdraw_modal.confirm")}
            </button>
          </div>
        </div>
        <button
          onClick={onClose}
          className="tw-absolute tw-right-4 tw-top-4 tw-text-gray-400 tw-transition-colors hover:tw-text-gray-600"
        >
          ✕
        </button>
      </motion.div>
    </div>
  );
};

const LoadingSkeleton = () => {
  const { t } = useTranslation();
  return (
    <div className="tw-min-h-screen tw-bg-gray-50">
      <StickyHeader withoutCta />
      <section className="tw-relative tw-h-screen tw-overflow-hidden">
        <div className="tw-absolute tw-inset-0 tw-animate-pulse tw-bg-gray-200" />
        <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
          <div className="tw-max-w-4xl tw-px-4 tw-text-center">
            <div className="tw-mx-auto tw-mb-4 tw-h-12 tw-w-3/4 tw-animate-pulse tw-rounded-lg tw-bg-gray-300" />
            <div className="tw-mx-auto tw-h-6 tw-w-1/2 tw-animate-pulse tw-rounded-lg tw-bg-gray-300" />
          </div>
        </div>
      </section>
      <div className="tw-mx-auto tw-max-w-6xl tw-px-4 tw-py-8">
        <div className="tw-space-y-8">
          <div className="tw-rounded-xl tw-bg-white tw-p-6 tw-shadow-sm">
            <div className="tw-mb-6 tw-h-8 tw-w-1/4 tw-animate-pulse tw-rounded-lg tw-bg-gray-200" />
            <div className="tw-grid tw-grid-cols-1 tw-gap-8 md:tw-grid-cols-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="tw-space-y-4">
                  <div className="tw-mx-auto tw-h-12 tw-w-12 tw-animate-pulse tw-rounded-full tw-bg-gray-200" />
                  <div className="tw-mx-auto tw-h-6 tw-w-3/4 tw-animate-pulse tw-rounded-lg tw-bg-gray-200" />
                  <div className="tw-h-4 tw-w-full tw-animate-pulse tw-rounded-lg tw-bg-gray-200" />
                </div>
              ))}
            </div>
          </div>
          <div className="tw-rounded-xl tw-bg-white tw-p-6 tw-shadow-sm">
            <div className="tw-mb-6 tw-h-8 tw-w-1/3 tw-animate-pulse tw-rounded-lg tw-bg-gray-200" />
            <div className="tw-grid tw-grid-cols-1 tw-gap-8 md:tw-grid-cols-2">
              {[1, 2].map((i) => (
                <div key={i} className="tw-space-y-4">
                  <div className="tw-h-6 tw-w-1/2 tw-animate-pulse tw-rounded-lg tw-bg-gray-200" />
                  <div className="tw-space-y-2">
                    {[1, 2, 3].map((j) => (
                      <div
                        key={j}
                        className="tw-h-4 tw-w-full tw-animate-pulse tw-rounded-lg tw-bg-gray-200"
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const HeroSection = ({ user, navigate, t }) => {
  return (
    <section className="tw-relative tw-h-screen tw-overflow-hidden">
      <div
        className="tw-absolute tw-inset-0 tw-scale-110 tw-transform tw-bg-cover tw-bg-center"
        style={{
          backgroundImage:
            "url('https://sotravel-agent-folder.s3.ap-southeast-1.amazonaws.com/%2Frandom/151028Copy%20of%20IMG_4012.JPG')",
          transform: "translateZ(-1px) scale(1)",
        }}
      />
      <div className="tw-absolute tw-inset-0 tw-bg-black/40" />
      <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center">
        <div className="tw-max-w-4xl tw-px-4 tw-text-center tw-text-white">
          <h1
            className="tw-mb-3 tw-text-[34px] tw-font-bold tw-leading-tight md:tw-text-[52px]"
            dangerouslySetInnerHTML={{ __html: t("affiliate.hero.title") }}
          />
          {user.influencer_type ? (
            <p className="tw-mx-auto tw-mb-5 tw-max-w-2xl tw-text-[18px] tw-text-gray-200 md:tw-text-[20px]">
              {t("affiliate.hero.existing_affiliate")}
            </p>
          ) : (
            <p className="tw-mx-auto tw-mb-5 tw-max-w-2xl tw-text-[18px] tw-text-gray-200 md:tw-text-[20px]">
              {t("affiliate.hero.description")}
            </p>
          )}
          {!user.id || !user.influencer_type ? (
            <button
              onClick={() =>
                navigate({ pathname: "/login", search: "?reroute=/request-affiliate" })
              }
              className="tw-rounded-full tw-bg-[#00BFFF] tw-px-6 tw-py-2.5 tw-text-[16px] tw-font-medium tw-text-white tw-transition tw-duration-300 hover:tw-bg-[#00A3D9]"
            >
              {t("affiliate.hero.cta")}
            </button>
          ) : null}
        </div>
      </div>
      <motion.div
        className="tw-absolute tw-bottom-8 tw-left-0 tw-right-0 tw-mx-auto tw-flex tw-w-fit tw-cursor-pointer tw-flex-col tw-items-center tw-gap-2"
        onClick={() => window.scrollTo({ top: window.innerHeight, behavior: "smooth" })}
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1 }}
      >
        <motion.p
          className="tw-text-sm tw-font-medium tw-tracking-wide tw-text-white/90"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          {t("affiliate.hero.scroll_text")}
        </motion.p>
        <motion.div
          className="tw-flex tw-flex-col tw-items-center tw-gap-1.5"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <div className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-white/90" />
          <div className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-white/70" />
          <div className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-white/50" />
        </motion.div>
      </motion.div>
    </section>
  );
};

const HowItWorksSection = ({ currency, t }) => {
  return (
    <section className="tw-mb-12">
      <h2 className="tw-text-deepskyblue tw-mb-8 tw-text-2xl tw-font-semibold">
        {t("affiliate.how_it_works.title")}
      </h2>
      <div className="tw-grid tw-grid-cols-1 tw-gap-8 md:tw-grid-cols-3">
        <div className="tw-rounded-xl tw-bg-white tw-p-6 tw-text-center tw-shadow-sm">
          <div className="tw-mb-4 tw-flex tw-justify-center">
            <div className="tw-rounded-full tw-bg-blue-50 tw-p-4">
              <Users className="tw-h-8 tw-w-8 tw-text-blue-500" />
            </div>
          </div>
          <h3 className="tw-mb-2 tw-text-lg tw-font-semibold">
            {t("affiliate.how_it_works.step1.title")}
          </h3>
          <p className="tw-text-gray-600">{t("affiliate.how_it_works.step1.description")}</p>
        </div>
        <div className="tw-rounded-xl tw-bg-white tw-p-6 tw-text-center tw-shadow-sm">
          <div className="tw-mb-4 tw-flex tw-justify-center">
            <div className="tw-rounded-full tw-bg-green-50 tw-p-4">
              <CheckCircle className="tw-h-8 tw-w-8 tw-text-green-500" />
            </div>
          </div>
          <h3 className="tw-mb-2 tw-text-lg tw-font-semibold">
            {t("affiliate.how_it_works.step2.title")}
          </h3>
          <p className="tw-text-gray-600">
            {t("affiliate.how_it_works.step2.description", {
              amount: getReferralAmount(currency, "welcome"),
            })}
          </p>
        </div>
        <div className="tw-rounded-xl tw-bg-white tw-p-6 tw-text-center tw-shadow-sm">
          <div className="tw-mb-4 tw-flex tw-justify-center">
            <div className="tw-rounded-full tw-bg-purple-50 tw-p-4">
              <DollarSign className="tw-h-8 tw-w-8 tw-text-purple-500" />
            </div>
          </div>
          <h3 className="tw-mb-2 tw-text-lg tw-font-semibold">
            {t("affiliate.how_it_works.step3.title")}
          </h3>
          <p className="tw-text-gray-600">{t("affiliate.how_it_works.step3.description")}</p>
        </div>
      </div>
    </section>
  );
};

const BenefitsSection = ({ currency, t }) => {
  return (
    <section className="tw-mb-12">
      <h2 className="tw-text-deepskyblue tw-mb-8 tw-text-2xl tw-font-semibold">
        {t("affiliate.benefits.title")}
      </h2>
      <div className="tw-grid tw-grid-cols-1 tw-gap-8 md:tw-grid-cols-2">
        <div className="tw-rounded-xl tw-bg-white tw-p-6 tw-shadow-sm">
          <h3 className="tw-mb-4 tw-text-lg tw-font-semibold">{t("affiliate.benefits.for_you")}</h3>
          <ul className="tw-space-y-3">
            {t("affiliate.benefits.benefits_you", { returnObjects: true }).map((benefit, index) => (
              <li key={index} className="tw-flex tw-items-center tw-gap-2">
                <CheckCircle className="tw-h-5 tw-w-5 tw-text-green-500" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="tw-rounded-xl tw-bg-white tw-p-6 tw-shadow-sm">
          <h3 className="tw-mb-4 tw-text-lg tw-font-semibold">
            {t("affiliate.benefits.for_audience")}
          </h3>
          <ul className="tw-space-y-3">
            {t("affiliate.benefits.benefits_audience", {
              returnObjects: true,
              amount: getReferralAmount(currency, "welcome"),
            }).map((benefit, index) => (
              <li key={index} className="tw-flex tw-items-center tw-gap-2">
                <CheckCircle className="tw-h-5 tw-w-5 tw-text-green-500" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

const AffiliateLinkSection = ({ inviteUrl, inviteMessage, changeCopyColor, copyIconColor, t }) => {
  return (
    <section className="tw-mb-8">
      <div className="tw-rounded-xl tw-bg-white tw-p-6 tw-shadow-sm">
        <h2 className="tw-text-deepskyblue tw-mb-4 tw-text-xl tw-font-semibold">
          {t("affiliate.affiliate_link.title")}
        </h2>
        <p className="tw-mb-4 tw-text-gray-600">{t("affiliate.affiliate_link.share_text")}</p>
        <CopyToClipboard text={inviteMessage} onCopy={changeCopyColor}>
          <button
            className="tw-font-manrope tw-flex tw-h-12 tw-w-full tw-items-center tw-justify-between tw-rounded-md tw-border tw-border-solid tw-bg-white tw-px-4 tw-py-2 tw-text-[15px] tw-font-normal tw-text-black"
            style={{ borderColor: copyIconColor }}
          >
            <span
              className="tw-inline-block tw-flex tw-h-full tw-w-[90%] tw-items-center tw-justify-start tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-text-left"
              style={{ color: copyIconColor }}
            >
              {inviteUrl}
            </span>
            <ContentCopyOutlinedIcon className="tw-text-[22px]" style={{ color: copyIconColor }} />
          </button>
        </CopyToClipboard>
      </div>
    </section>
  );
};

const AnalyticsStatsSection = ({ analyticsData, timeframe, earningsSummary, t }) => {
  return (
    <div className="tw-mb-8 tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-4">
      <motion.div
        whileHover={{ scale: 1.02 }}
        className="tw-rounded-xl tw-bg-white tw-p-6 tw-shadow-sm tw-transition-shadow hover:tw-shadow-md"
      >
        <div className="tw-mb-2 tw-flex tw-items-center tw-gap-3">
          <div className="tw-rounded-lg tw-bg-blue-50 tw-p-2">
            <Activity className="tw-h-5 tw-w-5 tw-text-blue-500" />
          </div>
          <h3 className="tw-text-gray-600">{t("affiliate.analytics.stats.clicks")}</h3>
        </div>
        <p className="tw-text-2xl tw-font-bold">{earningsSummary.totalClicks}</p>
        <p className="tw-text-sm tw-text-gray-500">{t("affiliate.analytics.timeframes.all")}</p>
      </motion.div>
      <motion.div
        whileHover={{ scale: 1.02 }}
        className="tw-rounded-xl tw-bg-white tw-p-6 tw-shadow-sm tw-transition-shadow hover:tw-shadow-md"
      >
        <div className="tw-mb-2 tw-flex tw-items-center tw-gap-3">
          <div className="tw-rounded-lg tw-bg-green-50 tw-p-2">
            <Users className="tw-h-5 tw-w-5 tw-text-green-500" />
          </div>
          <h3 className="tw-text-gray-600">{t("affiliate.analytics.stats.signups")}</h3>
        </div>
        <p className="tw-text-2xl tw-font-bold">{earningsSummary.totalSignups}</p>
        <p className="tw-text-sm tw-text-gray-500">{t("affiliate.analytics.timeframes.all")}</p>
      </motion.div>
      <motion.div
        whileHover={{ scale: 1.02 }}
        className="tw-rounded-xl tw-bg-white tw-p-6 tw-shadow-sm tw-transition-shadow hover:tw-shadow-md"
      >
        <div className="tw-mb-2 tw-flex tw-items-center tw-gap-3">
          <div className="tw-rounded-lg tw-bg-purple-50 tw-p-2">
            <BarChart2 className="tw-h-5 tw-w-5 tw-text-purple-500" />
          </div>
          <h3 className="tw-text-gray-600">{t("affiliate.analytics.stats.bookings")}</h3>
        </div>
        <p className="tw-text-2xl tw-font-bold">{earningsSummary.totalBookings}</p>
        <p className="tw-text-sm tw-text-gray-500">{t("affiliate.analytics.timeframes.all")}</p>
      </motion.div>
      {/* <motion.div whileHover={{ scale: 1.02 }} className="tw-bg-white tw-p-6 tw-rounded-xl tw-shadow-sm hover:tw-shadow-md tw-transition-shadow">
        <div className="tw-flex tw-items-center tw-gap-3 tw-mb-2">
          <div className="tw-bg-yellow-50 tw-p-2 tw-rounded-lg">
            <LineChartIcon className="tw-text-yellow-500 tw-w-5 tw-h-5" />
          </div>
          <h3 className="tw-text-gray-600">{t("affiliate.analytics.stats.avg_booking_value", { defaultValue: "Avg. Booking Value" })}</h3>
        </div>
        <p className="tw-text-2xl tw-font-bold">${earningsSummary.totalBookings > 0 ? (parseFloat(earningsSummary.totalEarnings) / earningsSummary.totalBookings).toFixed(2) : "0.00"}</p>
        <p className="tw-text-sm tw-text-gray-500">{t("affiliate.analytics.stats.per_booking", { defaultValue: "Per booking" })}</p>
      </motion.div> */}
    </div>
  );
};

const AnalyticsSection = ({
  isLoading,
  timeframe,
  setTimeframe,
  analyticsData,
  earningsSummary,
  referrals,
  referralTotal,
  currentPage,
  onPageChange,
  pageSize,
  t,
}) => {
  if (isLoading) {
    return (
      <div className="tw-flex tw-h-64 tw-flex-col tw-items-center tw-justify-center tw-space-y-4">
        <p className="tw-animate-pulse tw-font-medium tw-text-gray-500">
          {t("affiliate.analytics.loading", { defaultValue: "Loading analytics..." })}
        </p>
      </div>
    );
  }

  return (
    <section className="tw-mb-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="tw-mb-8">
          <h2 className="tw-text-deepskyblue tw-mb-4 tw-text-2xl tw-font-semibold">
            {t("affiliate.analytics.title")}
          </h2>
          <AnalyticsStatsSection
            analyticsData={analyticsData}
            timeframe={timeframe}
            earningsSummary={earningsSummary}
            t={t}
          />
        </div>
        <div className="tw-mb-8">
          <div className="tw-mb-6 tw-flex tw-items-center tw-justify-between">
            <h2 className="tw-text-deepskyblue tw-text-2xl tw-font-semibold">
              {t("affiliate.analytics.time_based_title", { defaultValue: "Time-based Analytics" })}
            </h2>
            <Select
              value={timeframe}
              onChange={(option) => setTimeframe(option.value)}
              options={[
                { value: "daily", label: t("affiliate.analytics.timeframes.7d") },
                { value: "weekly", label: t("affiliate.analytics.timeframes.30d") },
                { value: "monthly", label: t("affiliate.analytics.timeframes.90d") },
                { value: "all_time", label: t("affiliate.analytics.timeframes.all") },
              ]}
              className="tw-min-w-[120px]"
            />
          </div>
          <div className="tw-grid tw-grid-cols-1 tw-gap-6 md:tw-grid-cols-2">
            <div className="tw-rounded-xl tw-bg-white tw-p-6 tw-shadow-sm">
              <h3 className="tw-mb-4 tw-text-lg tw-font-semibold">
                {t("affiliate.analytics.earnings.title")}
              </h3>
              <div style={{ width: "100%", height: 300 }}>
                {analyticsData[timeframe]?.length > 0 &&
                analyticsData[timeframe].some(
                  (data) =>
                    parseFloat(data.earningsCash) > 0 || parseFloat(data.earningsCredits) > 0
                ) ? (
                  <ResponsiveContainer>
                    <ComposedChart
                      data={analyticsData[timeframe]}
                      margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="colorEarningsCash" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#00BFFF" stopOpacity={0.8} />
                          <stop offset="95%" stopColor="#00BFFF" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="colorEarningsCredits" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8} />
                          <stop offset="95%" stopColor="#82ca9d" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="date" />
                      <YAxis />
                      <CartesianGrid strokeDasharray="3 3" />
                      <Tooltip
                        formatter={(value, name) =>
                          name === "Cash Earnings"
                            ? [
                                `$${value}`,
                                t("affiliate.analytics.earnings.cash", {
                                  defaultValue: "Cash Earnings",
                                }),
                              ]
                            : [
                                `${value} credits`,
                                t("affiliate.analytics.earnings.credits", {
                                  defaultValue: "Credit Earnings",
                                }),
                              ]
                        }
                      />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="earningsCash"
                        name={t("affiliate.analytics.earnings.cash", {
                          defaultValue: "Cash Earnings",
                        })}
                        stroke="#00BFFF"
                        fillOpacity={1}
                        fill="url(#colorEarningsCash)"
                      />
                      <Line
                        type="monotone"
                        dataKey="earningsCredits"
                        name={t("affiliate.analytics.earnings.credits", {
                          defaultValue: "Credit Earnings",
                        })}
                        stroke="#82ca9d"
                        strokeWidth={2}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-center tw-text-gray-500">
                    <LineChartIcon className="tw-mb-3 tw-h-12 tw-w-12 tw-opacity-50" />
                    <p className="tw-text-lg tw-font-medium">
                      {t("affiliate.analytics.earnings.no_data", {
                        defaultValue: "No earnings data yet",
                      })}
                    </p>
                    <p className="tw-mt-1 tw-text-sm">
                      {t("affiliate.analytics.earnings.no_data_message", {
                        defaultValue: "Start sharing your affiliate link to earn rewards",
                      })}
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div className="tw-rounded-xl tw-bg-white tw-p-6 tw-shadow-sm">
              <h3 className="tw-mb-4 tw-text-lg tw-font-semibold">
                {t("affiliate.analytics.conversion_title", { defaultValue: "Conversion Metrics" })}
              </h3>
              <div style={{ width: "100%", height: 300 }}>
                {analyticsData[timeframe]?.length > 0 &&
                analyticsData[timeframe].some(
                  (data) =>
                    parseInt(data.clicks) > 0 ||
                    parseInt(data.signups) > 0 ||
                    parseInt(data.bookings) > 0
                ) ? (
                  <ResponsiveContainer>
                    <LineChart data={analyticsData[timeframe]}>
                      <XAxis dataKey="date" />
                      <YAxis />
                      <CartesianGrid strokeDasharray="3 3" />
                      <Tooltip />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="clicks"
                        stroke="#8884d8"
                        name={t("affiliate.analytics.stats.clicks")}
                      />
                      <Line
                        type="monotone"
                        dataKey="signups"
                        stroke="#82ca9d"
                        name={t("affiliate.analytics.stats.signups")}
                      />
                      <Line
                        type="monotone"
                        dataKey="bookings"
                        stroke="#ffc658"
                        name={t("affiliate.analytics.stats.bookings")}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-center tw-text-gray-500">
                    <BarChart2 className="tw-mb-3 tw-h-12 tw-w-12 tw-opacity-50" />
                    <p className="tw-text-lg tw-font-medium">
                      {t("affiliate.analytics.conversion_no_data", {
                        defaultValue: "No conversion data yet",
                      })}
                    </p>
                    <p className="tw-mt-1 tw-text-sm">
                      {t("affiliate.analytics.conversion_no_data_message", {
                        defaultValue: "Share your link to start tracking conversions",
                      })}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="tw-rounded-xl tw-bg-white tw-p-6 tw-shadow-sm">
          <h3 className="tw-mb-4 tw-text-xl tw-font-semibold">
            {t("affiliate.analytics.referrals.title")}
          </h3>
          <ReferralsTable
            referrals={referrals}
            total={referralTotal}
            currentPage={currentPage}
            onPageChange={onPageChange}
            pageSize={pageSize}
            t={t}
          />
        </div>
      </motion.div>
    </section>
  );
};

export default AffiliateProgram;
